import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import VisitService from "../services/VisitService";
import PatientService from "../services/PatientService";

const treatmentNames = {
  BodyAcupuncture: "Body Acupuncture",
  Auriculotherapy: "Aurikuloterapija",
  AestheticMedicine: "Estetska medicina",
};

const getTreatmentDisplayName = (item) => {
  const dto = item.treatmentTypeDTO;
  if (!dto) return "Nepoznat tretman";
  if (dto.typeName && dto.typeName !== "TreatmentType" && dto.typeName.trim() !== "") {
    const cleanKey = dto.typeName.trim();
    return treatmentNames[cleanKey] || `Tretman: ${cleanKey}`;
  }
  if (dto.name) return treatmentNames[dto.name.trim()] || `Tretman: ${dto.name.trim()}`;
  if (item.treatmentType) return treatmentNames[item.treatmentType.trim()] || `Tretman: ${item.treatmentType.trim()}`;
  return "Nepoznat tretman";
};

function PatientVisits() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [patient, setPatient] = useState(null);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [editData, setEditData] = useState({});

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
  };

  useEffect(() => {
    const loadVisits = async () => {
      try {
        const data = await VisitService.getAllVisitsByPatientId(id);
        setVisits(data || []);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    loadVisits();
  }, [id]);

  useEffect(() => {
    const loadPatient = async () => {
      try {
        const response = await PatientService.getPatientById(id);
        setPatient(response || null);
      } catch (error) {
        console.error(error);
      }
    };
    loadPatient();
  }, [id]);

  if (loading) return <p>Učitavanje poseta...</p>;

  const buttonStyle = {
    padding: "10px 18px",
    margin: "0 5px",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    fontWeight: "bold",
    backgroundColor: "#007bff",
    color: "white",
    transition: "0.2s",
  };

  const sortedVisits = [...visits].sort((a, b) => new Date(b.visitDate) - new Date(a.visitDate));

  const openPopup = (visit) => {
    setSelectedVisit(visit);
    const items = visit.visitItemDTOS || [];
    const initialData = {};
    items.forEach((item, idx) => {
      initialData[idx] = { ...item.treatmentTypeDTO };
    });
    setEditData(initialData);
  };

  const closePopup = () => {
    setSelectedVisit(null);
    setEditData({});
  };

  const handleChange = (index, field, value) => {
    setEditData((prev) => ({
      ...prev,
      [index]: { ...prev[index], [field]: value },
    }));
  };

  const isEditable = (visitDate) => {
    const diff = (new Date() - new Date(visitDate)) / (1000 * 60 * 60 * 24);
    return diff <= 7;
  };

  const saveChanges = async () => {
    if (!selectedVisit) return;

    try {
      const visitDTO = {
        visitID: selectedVisit.visitID,
        visitDate: selectedVisit.visitDate,
        notes: selectedVisit.notes,
        doctorDTO: { doctorID: selectedVisit.doctorDTO?.doctorID },
        patientDTO: { patientID: selectedVisit.patientDTO?.patientID },
        visitItemDTOS: selectedVisit.visitItemDTOS.map((item, idx) => ({
          treatmentTypeDTO: editData[idx],
        })),
      };

      await VisitService.updateVisit(selectedVisit.visitID, visitDTO);
      const updatedVisits = await VisitService.getAllVisitsByPatientId(id);
      setVisits(updatedVisits);

      alert("Izmene sačuvane!");
      closePopup();
    } catch (error) {
      console.error("Greška pri update-u posete:", error);
      alert("Greška pri čuvanju izmena.");
    }
  };

  const renderParameters = (item, index, editable) => {
    const dto = editData[index] || {};
    const type = item.treatmentTypeDTO?.typeName;
    const styleLabel = { display: "block", marginBottom: "8px", fontWeight: "500" };
    const styleInput = {
      width: "100%",
      padding: "6px 8px",
      marginTop: "2px",
      borderRadius: "4px",
      border: "1px solid #ccc",
      backgroundColor: editable ? "white" : "#f0f0f0",
    };

    switch (type) {
      case "Auriculotherapy":
        return (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
            <label style={styleLabel}>Težina (kg):
              <input style={styleInput} type="text" value={dto.weight ?? ""} disabled={!editable} onChange={(e) => handleChange(index, "weight", e.target.value)} />
            </label>
            <label style={styleLabel}>BMI:
              <input style={styleInput} type="text" value={dto.bmi ?? ""} disabled={!editable} onChange={(e) => handleChange(index, "bmi", e.target.value)} />
            </label>
            <label style={styleLabel}>Struk (cm):
              <input style={styleInput} type="text" value={dto.waist ?? ""} disabled={!editable} onChange={(e) => handleChange(index, "waist", e.target.value)} />
            </label>
            <label style={styleLabel}>Body Fat (%):
              <input style={styleInput} type="text" value={dto.bodyFat ?? ""} disabled={!editable} onChange={(e) => handleChange(index, "bodyFat", e.target.value)} />
            </label>
            <label style={styleLabel}>Visceral Fat:
              <input style={styleInput} type="text" value={dto.visceralFat ?? ""} disabled={!editable} onChange={(e) => handleChange(index, "visceralFat", e.target.value)} />
            </label>
            <label style={styleLabel}>Protein (%):
              <input style={styleInput} type="text" value={dto.protein ?? ""} disabled={!editable} onChange={(e) => handleChange(index, "protein", e.target.value)} />
            </label>
            {/* NOVI PARAMETRI */}
            <label style={styleLabel}>Kukovi (cm):
              <input style={styleInput} type="text" value={dto.hipCircumference ?? ""} disabled={!editable} onChange={(e) => handleChange(index, "hipCircumference", e.target.value)} />
            </label>
            <label style={styleLabel}>Stomak (cm):
              <input style={styleInput} type="text" value={dto.abdomenCircumference ?? ""} disabled={!editable} onChange={(e) => handleChange(index, "abdomenCircumference", e.target.value)} />
            </label>
            <label style={styleLabel}>Grudi (cm):
              <input style={styleInput} type="text" value={dto.chestCircumference ?? ""} disabled={!editable} onChange={(e) => handleChange(index, "chestCircumference", e.target.value)} />
            </label>
            {/* JELOVNIK - Zauzima dve kolone */}
            <label style={{ ...styleLabel, gridColumn: "span 2" }}>Jelovnik / Ishrana:
              <textarea 
                style={{ ...styleInput, minHeight: "80px", fontFamily: "inherit" }} 
                value={dto.dietPlan ?? ""} 
                disabled={!editable} 
                onChange={(e) => handleChange(index, "dietPlan", e.target.value)} 
              />
            </label>
          </div>
        );
      case "BodyAcupuncture":
        return (
          <label style={styleLabel}>Akupunkturne tačke:
            <input style={styleInput} type="text" value={dto.acupuncturePoints ?? ""} disabled={!editable} onChange={(e) => handleChange(index, "acupuncturePoints", e.target.value)} />
          </label>
        );
      case "AestheticMedicine":
        return (
          <label style={styleLabel}>Opis procedure:
            <input style={styleInput} type="text" value={dto.procedureDescription ?? ""} disabled={!editable} onChange={(e) => handleChange(index, "procedureDescription", e.target.value)} />
          </label>
        );
      default:
        return <p>Nema parametara za prikaz</p>;
    }
  };

  return (
    <div style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}>
      <h2 style={{ textAlign: "center", marginBottom: "20px" }}>
        Posete pacijenta {patient ? `${patient.name} ${patient.surname}` : ""}
      </h2>

      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <button onClick={() => navigate(`/patients/${id}/aurikulotherapy`)} style={buttonStyle}>Aurikuloterapije</button>
        <button onClick={() => navigate(`/patients/${id}/estetska-medicina`)} style={buttonStyle}>Estetska medicina</button>
        <button onClick={() => navigate(`/patients/${id}/body-acupuncture`)} style={buttonStyle}>Body Acupuncture</button>
      </div>

      {sortedVisits.length === 0 ? <p>Nema evidentiranih poseta.</p> : (
        <div>
          {sortedVisits.map((visit) => (
            <div
              key={visit.visitID}
              style={{
                background: "linear-gradient(135deg, #f9f9f9, #e6f0ff)",
                borderRadius: "12px",
                padding: "18px",
                marginBottom: "15px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                textAlign: "left",
                position: "relative",
                cursor: "pointer",
                transition: "transform 0.2s",
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.02)"}
              onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
              onClick={() => openPopup(visit)}
            >
              <div style={{
                position: "absolute",
                top: "15px",
                right: "15px",
                backgroundColor: "#007bff",
                color: "white",
                padding: "4px 10px",
                borderRadius: "12px",
                fontSize: "0.85em",
                fontWeight: "600",
              }}>{formatDate(visit.visitDate)}</div>

              <div>
                <strong>Doktor:</strong> {visit.doctorDTO?.firstName} {visit.doctorDTO?.lastName}<br/>
                <strong>Napomena:</strong> {visit.notes || "N/A"}
              </div>

              <div style={{ marginTop: "10px" }}>
                <strong>Stavke posete:</strong>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "5px" }}>
                  {(visit.visitItemDTOS || []).map((item, idx) => (
                    <span key={idx} style={{
                      backgroundColor: "#cce5ff",
                      color: "#004085",
                      padding: "5px 12px",
                      borderRadius: "16px",
                      fontSize: "0.9em",
                      fontWeight: "500",
                      border: "1px solid #004085",
                    }}>{getTreatmentDisplayName(item)}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <button onClick={() => navigate(-1)} style={{
        marginTop: "20px",
        padding: "10px 22px",
        cursor: "pointer",
        borderRadius: "8px",
        backgroundColor: "#dc3545",
        color: "white",
        border: "none",
        fontWeight: "bold",
      }}>Nazad</button>

      {/* POPUP */}
      {selectedVisit && (
        <div style={{
          position: "fixed",
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
        }}>
          <div style={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "20px",
            width: "600px",
            maxHeight: "90%",
            overflowY: "auto",
            boxShadow: "0 10px 25px rgba(0,0,0,0.2)"
          }}>
            <h3 style={{ marginBottom: "15px", borderBottom: "2px solid #007bff", paddingBottom: "10px" }}>
              Detalji posete ({formatDate(selectedVisit.visitDate)})
            </h3>
            {(selectedVisit.visitItemDTOS || []).map((item, idx) => (
              <div key={idx} style={{ marginBottom: "20px", borderBottom: "1px solid #eee", paddingBottom: "10px" }}>
                <div style={{ marginBottom: "10px", color: "#007bff", fontSize: "1.1em" }}>
                  <strong>{getTreatmentDisplayName(item)}</strong>
                </div>
                {renderParameters(item, idx, isEditable(selectedVisit.visitDate))}
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
              {isEditable(selectedVisit.visitDate) && (
                <button onClick={saveChanges} style={{ padding: "10px 20px", borderRadius: "6px", backgroundColor: "#28a745", color: "white", border: "none", cursor: "pointer", fontWeight: "bold" }}>
                  Sačuvaj izmene
                </button>
              )}
              <button onClick={closePopup} style={{ padding: "10px 20px", borderRadius: "6px", backgroundColor: "#dc3545", color: "white", border: "none", cursor: "pointer", fontWeight: "bold" }}>
                Zatvori
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PatientVisits;